// Help management module for PPT Hero game
export default class HelpManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Show help popup
  showHelpPopup() {
    try {
      // Create help background
      this.scene.helpBackground = this.scene.add.rectangle(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        this.scene.cameras.main.width * 0.8,
        this.scene.cameras.main.height * 0.8,
        0x000000,
        0.9
      ).setDepth(1000);

      // Create help title
      this.scene.helpTitle = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2 - 200,
        "PPT Hero - Help & Tutorial",
        this.scene.getProfessionalTextStyle("28px", "#ffffff", "700")
      ).setOrigin(0.5).setDepth(1001);

      // Create help content
      const helpContent = this.getHelpContent();
      this.scene.helpContent = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        helpContent,
        this.scene.getProfessionalTextStyle("16px", "#ffffff", "400")
      ).setOrigin(0.5).setDepth(1001);

      // Create close button
      this.scene.helpCloseButton = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2 + 200,
        "Click to Close",
        this.scene.getProfessionalTextStyle("18px", "#00d4ff", "600")
      ).setOrigin(0.5).setDepth(1001);

      // Make close button interactive
      this.scene.helpCloseButton.setInteractive({ useHandCursor: true });
      this.scene.helpCloseButton.on('pointerdown', () => {
        this.hideHelpPopup();
      });

      // Add escape key handler
      this.scene.input.keyboard.once('keydown-ESC', () => {
        this.hideHelpPopup();
      });

    } catch (error) {
      console.error('Error showing help popup:', error);
    }
  }

  // Hide help popup
  hideHelpPopup() {
    try {
      if (this.scene.helpBackground) {
        this.scene.helpBackground.destroy();
        this.scene.helpBackground = null;
      }
      if (this.scene.helpTitle) {
        this.scene.helpTitle.destroy();
        this.scene.helpTitle = null;
      }
      if (this.scene.helpContent) {
        this.scene.helpContent.destroy();
        this.scene.helpContent = null;
      }
      if (this.scene.helpCloseButton) {
        this.scene.helpCloseButton.destroy();
        this.scene.helpCloseButton = null;
      }
    } catch (error) {
      console.error('Error hiding help popup:', error);
    }
  }

  // Get help content
  getHelpContent() {
    return `🎯 OBJECTIVE
Complete levels by using PowerPoint keyboard shortcuts to manipulate shapes.

🎮 CONTROLS
• Click and drag shapes to move them
• Use keyboard shortcuts for advanced operations
• Press ESC to clear selection
• Press SPACE to pause/resume

⌨️ KEYBOARD SHORTCUTS
• Ctrl+A: Select all shapes
• Alt+H+G+A+L: Align shapes to left
• Alt+H+G+A+R: Align shapes to right
• Alt+H+G+A+T: Align shapes to top
• Alt+H+G+A+B: Align shapes to bottom
• Alt+H+G+A+C: Center shapes horizontally
• Alt+H+G+A+M: Center shapes vertically
• Alt+H+G+A+H: Distribute shapes horizontally
• Alt+H+G+A+V: Distribute shapes vertically

🏆 SCORING
• Base score: 100 points per level
• Time bonus: +2 points per second remaining
• Shortcut bonus: +10 points per unique shortcut used
• Error penalty: -5 points per mistake

💡 TIPS
• Practice makes perfect!
• Use shortcuts instead of mouse for higher scores
• Complete levels quickly for time bonuses
• Try to use multiple shortcuts per level`;
  }

  // Show tutorial for current level
  showLevelTutorial() {
    try {
      const levelData = this.scene.levels[this.scene.currentLevel];
      if (!levelData || !levelData.tutorial) return;

      // Create tutorial overlay
      this.scene.tutorialOverlay = this.scene.add.rectangle(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        this.scene.cameras.main.width * 0.7,
        this.scene.cameras.main.height * 0.6,
        0x000000,
        0.8
      ).setDepth(800);

      // Create tutorial title
      this.scene.tutorialTitle = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2 - 150,
        `Level ${this.scene.currentLevel} Tutorial`,
        this.scene.getProfessionalTextStyle("24px", "#00d4ff", "700")
      ).setOrigin(0.5).setDepth(801);

      // Create tutorial content
      this.scene.tutorialContent = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        levelData.tutorial,
        this.scene.getProfessionalTextStyle("18px", "#ffffff", "400")
      ).setOrigin(0.5).setDepth(801);

      // Create continue button
      this.scene.tutorialContinueButton = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2 + 150,
        "Click to Continue",
        this.scene.getProfessionalTextStyle("18px", "#00ff88", "600")
      ).setOrigin(0.5).setDepth(801);

      // Make continue button interactive
      this.scene.tutorialContinueButton.setInteractive({ useHandCursor: true });
      this.scene.tutorialContinueButton.on('pointerdown', () => {
        this.hideLevelTutorial();
      });

      // Auto-hide after 10 seconds
      this.scene.time.delayedCall(10000, () => {
        this.hideLevelTutorial();
      });

    } catch (error) {
      console.error('Error showing level tutorial:', error);
    }
  }

  // Hide level tutorial
  hideLevelTutorial() {
    try {
      if (this.scene.tutorialOverlay) {
        this.scene.tutorialOverlay.destroy();
        this.scene.tutorialOverlay = null;
      }
      if (this.scene.tutorialTitle) {
        this.scene.tutorialTitle.destroy();
        this.scene.tutorialTitle = null;
      }
      if (this.scene.tutorialContent) {
        this.scene.tutorialContent.destroy();
        this.scene.tutorialContent = null;
      }
      if (this.scene.tutorialContinueButton) {
        this.scene.tutorialContinueButton.destroy();
        this.scene.tutorialContinueButton = null;
      }
    } catch (error) {
      console.error('Error hiding level tutorial:', error);
    }
  }

  // Show shortcut help
  showShortcutHelp() {
    try {
      // Create shortcut help popup
      this.scene.shortcutHelpBackground = this.scene.add.rectangle(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        this.scene.cameras.main.width * 0.6,
        this.scene.cameras.main.height * 0.7,
        0x1a252f,
        0.95
      ).setDepth(1000);

      // Create shortcut help title
      this.scene.shortcutHelpTitle = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2 - 200,
        "Keyboard Shortcuts",
        this.scene.getProfessionalTextStyle("24px", "#00d4ff", "700")
      ).setOrigin(0.5).setDepth(1001);

      // Get shortcuts from shortcut manager
      const shortcuts = this.scene.getShortcuts();
      const shortcutDescriptions = this.scene.getShortcutDescriptions();
      
      let shortcutText = "";
      Object.entries(shortcuts).forEach(([key, func]) => {
        const description = shortcutDescriptions[key] || "No description available";
        shortcutText += `${key}: ${description}\n\n`;
      });

      // Create shortcut content
      this.scene.shortcutHelpContent = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        shortcutText,
        this.scene.getProfessionalTextStyle("16px", "#ffffff", "400")
      ).setOrigin(0.5).setDepth(1001);

      // Create close button
      this.scene.shortcutHelpCloseButton = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2 + 200,
        "Click to Close",
        this.scene.getProfessionalTextStyle("18px", "#00d4ff", "600")
      ).setOrigin(0.5).setDepth(1001);

      // Make close button interactive
      this.scene.shortcutHelpCloseButton.setInteractive({ useHandCursor: true });
      this.scene.shortcutHelpCloseButton.on('pointerdown', () => {
        this.hideShortcutHelp();
      });

    } catch (error) {
      console.error('Error showing shortcut help:', error);
    }
  }

  // Hide shortcut help
  hideShortcutHelp() {
    try {
      if (this.scene.shortcutHelpBackground) {
        this.scene.shortcutHelpBackground.destroy();
        this.scene.shortcutHelpBackground = null;
      }
      if (this.scene.shortcutHelpTitle) {
        this.scene.shortcutHelpTitle.destroy();
        this.scene.shortcutHelpTitle = null;
      }
      if (this.scene.shortcutHelpContent) {
        this.scene.shortcutHelpContent.destroy();
        this.scene.shortcutHelpContent = null;
      }
      if (this.scene.shortcutHelpCloseButton) {
        this.scene.shortcutHelpCloseButton.destroy();
        this.scene.shortcutHelpCloseButton = null;
      }
    } catch (error) {
      console.error('Error hiding shortcut help:', error);
    }
  }

  // Show tooltip
  showTooltip(text, x, y, duration = 2000) {
    try {
      // Create tooltip background
      const tooltipBg = this.scene.add.rectangle(
        x,
        y - 30,
        200,
        40,
        0x000000,
        0.8
      ).setDepth(1000);

      // Create tooltip text
      const tooltipText = this.scene.add.text(
        x,
        y - 30,
        text,
        this.scene.getProfessionalTextStyle("14px", "#ffffff", "400")
      ).setOrigin(0.5).setDepth(1001);

      // Auto-remove after duration
      this.scene.time.delayedCall(duration, () => {
        tooltipBg.destroy();
        tooltipText.destroy();
      });

    } catch (error) {
      console.error('Error showing tooltip:', error);
    }
  }

  // Show context-sensitive help
  showContextHelp(context) {
    try {
      let helpText = "";
      
      switch (context) {
        case 'shape-selection':
          helpText = "Click on shapes to select them. Hold Shift to select multiple.";
          break;
        case 'shortcut-usage':
          helpText = "Use keyboard shortcuts for faster operations and higher scores.";
          break;
        case 'level-completion':
          helpText = "Complete the level objective to proceed to the next level.";
          break;
        case 'scoring':
          helpText = "Earn points by completing levels quickly and using shortcuts.";
          break;
        default:
          helpText = "Need help? Press F1 for the help menu.";
      }

      this.scene.showCenterNotification(helpText, 'info', 3000);

    } catch (error) {
      console.error('Error showing context help:', error);
    }
  }

  // Check if help is currently visible
  isHelpVisible() {
    return !!(this.scene.helpBackground || this.scene.tutorialOverlay || this.scene.shortcutHelpBackground);
  }

  // Hide all help elements
  hideAllHelp() {
    this.hideHelpPopup();
    this.hideLevelTutorial();
    this.hideShortcutHelp();
  }
}



